import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

const columnFilters = z
  .array(
    z.union([
      z.object({
        id: z.enum(["active", "is_staff", "countersignatory_number_IS_SET"]),
        value: z.boolean(),
      }),
      z.object({
        id: z.enum([
          "email",
          "given_name",
          "family_name",
          "known_customer_names",
          "admin_customer_names",
        ]),
        value: z.string(),
      }),
      z.object({
        id: z.enum(["known_customer_ids", "admin_customer_ids"]),
        value: z.string().uuid(),
      }),
      z.object({
        id: z.enum(["created_at", "updated_at"]),
        from: z.string().datetime({ offset: true }).optional(),
        to: z.string().datetime({ offset: true }).optional(),
      }),
    ])
  )
  .optional();
const ProfileDataEmailPreferences = z
  .object({ application_tracking: z.boolean() })
  .partial();
const ProfileData = z
  .object({ email_preferences: ProfileDataEmailPreferences })
  .partial();
const Profile = z.object({
  id: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  active: z.boolean(),
  is_staff: z.boolean(),
  known_customer_ids: z.array(z.string()),
  admin_customer_ids: z.array(z.string()),
  case_worker_customer_ids: z.array(z.string()),
  email: z.string(),
  given_name: z.string(),
  family_name: z.string(),
  countersignatory_number: z.string().optional(),
  data: ProfileData,
});
const _PageableMeta = z.object({
  totalRowCount: z.number().int(),
  page: z.object({ size: z.number().int(), index: z.number().int() }),
});
const Sort_GetProfiles = z.enum([
  "created_at asc",
  "created_at desc",
  "updated_at asc",
  "updated_at desc",
  "active asc",
  "active desc",
]);
const ColumnFilters_GetProfiles = z.array(
  z.union([
    z.object({
      id: z.enum(["active", "is_staff", "countersignatory_number_IS_SET"]),
      value: z.boolean(),
    }),
    z.object({
      id: z.enum([
        "email",
        "given_name",
        "family_name",
        "known_customer_names",
        "admin_customer_names",
      ]),
      value: z.string(),
    }),
    z.object({
      id: z.enum(["known_customer_ids", "admin_customer_ids"]),
      value: z.string().uuid(),
    }),
    z.object({
      id: z.enum(["created_at", "updated_at"]),
      from: z.string().datetime({ offset: true }).optional(),
      to: z.string().datetime({ offset: true }).optional(),
    }),
  ])
);
const ReturnType_GetProfiles = z.object({
  rows: z.array(
    Profile.and(
      z.object({
        known_customer_names: z.array(z.string()),
        admin_customer_names: z.array(z.string()),
      })
    )
  ),
  meta: _PageableMeta,
  sort: Sort_GetProfiles.optional(),
  globalFilter: z.string().optional(),
  columnFilters: ColumnFilters_GetProfiles.optional(),
});
const ErrorResponse = z.object({
  error: z.object({ type: z.string(), message: z.string() }).partial(),
});
const ProfileResponse = z.object({
  id: z.string(),
  identity_ids: z.array(z.string()),
  is_staff: z.boolean(),
  known_customer_ids: z.array(z.string()),
  admin_customer_ids: z.array(z.string()),
  case_worker_customer_ids: z.array(z.string()),
  email: z.string(),
  given_name: z.string(),
  family_name: z.string(),
  countersignatory_number: z.string().optional(),
  data: z.object({}).partial(),
  jwt: z.string().optional(),
});
const createMyProfile_Body = z.object({
  given_name: z.string(),
  family_name: z.string(),
});
const ApplicationStats = z.object({
  totalDraft: z.number().int(),
  totalAwaitingCustomerApproval: z.number().int(),
  totalAwaitingAutomatedVerification: z.number().int(),
  totalAwaitingPayment: z.number().int(),
  totalNeedsChanges: z.number().int(),
  totalAwaitingCountersigning: z.number().int(),
  totalAwaitingDBSSubmission: z.number().int(),
  totalAwaitingDBSResponse: z.number().int(),
  totalErrorFromDBS: z.number().int(),
  totalRejected: z.number().int(),
  totalRestarted: z.number().int(),
  totalInProgress: z.number().int(),
  totalCompleted: z.number().int(),
  totalUninvoiced: z.number().int(),
  totalAwaitingISA: z.number().int(),
});
const ReturnType_GetGlobalStats = z.object({
  stats: z.object({
    customers: z.object({ totalActive: z.number().int() }),
    jobs: z.object({ totalActive: z.number().int() }),
    invites: z.object({
      totalActiveStaff: z.number().int(),
      totalActiveCustomers: z.number().int(),
      totalActiveApplicants: z.number().int(),
    }),
    applications: ApplicationStats,
  }),
});
const ReturnType_GetCurrentUsageStats = z.object({
  usersWithLastAccess: z.object({
    within5mins: z.number().int(),
    within30mins: z.number().int(),
    within2hours: z.number().int(),
  }),
  activeCheckoutSessions: z.array(
    z.object({
      id: z.string(),
      created_at: z.string().datetime({ offset: true }).optional(),
    })
  ),
});
const NewInviteStaff = z.object({
  type: z.literal("staff"),
  email: z.string(),
});
const MyInviteStaff = z.object({ id: z.string() }).and(NewInviteStaff);
const NewInviteCustomer = z.object({
  id: z.string().optional(),
  type: z.literal("customer"),
  email: z.string(),
  customer_id: z.string(),
  case_worker: z.boolean().optional(),
});
const MyInviteCustomer = z
  .object({ id: z.string(), customer_name: z.string() })
  .and(NewInviteCustomer);
const EmploymentType = z.enum([
  "employee",
  "self_employed",
  "personal_employee",
]);
const NewInviteApplicant = z.object({
  id: z.string().optional(),
  type: z.literal("applicant"),
  email: z.string(),
  customer_id: z.string(),
  job_id: z.string(),
  test_application: z.boolean().optional(),
  case_worker_profile_id: z.string().uuid().optional(),
  employment_type: EmploymentType.optional(),
});
const MyInviteApplicant = z
  .object({
    id: z.string(),
    customer_name: z.string(),
    job_role: z.string(),
    test_application: z.boolean().optional(),
    restart_of_application_id: z.string().optional(),
  })
  .and(NewInviteApplicant);
const SetCaseWorker = z.object({
  case_worker_profile_id: z.string().uuid().nullable(),
});
const columnFilters__2 = z
  .array(
    z.union([
      z.object({
        id: z.literal("type"),
        value: z.enum(["staff", "customer", "applicant"]),
      }),
      z.object({ id: z.literal("employment_type"), value: EmploymentType }),
      z.object({ id: z.literal("active"), value: z.boolean() }),
      z.object({
        id: z.enum(["job_id", "case_worker_profile_id"]),
        value: z.string().uuid(),
      }),
      z.object({
        id: z.enum([
          "email",
          "customer_name",
          "job_role",
          "invited_by_profile_name",
        ]),
        value: z.string(),
      }),
      z.object({
        id: z.enum(["accepted_at", "invited_at"]),
        from: z.string().datetime({ offset: true }).optional(),
        to: z.string().datetime({ offset: true }).optional(),
      }),
    ])
  )
  .optional();
const _InviteCommon = z.object({
  id: z.string(),
  active: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  accepted_at: z.string().datetime({ offset: true }).optional(),
  accepted_by_profile_id: z.string().optional(),
  accepted_by_profile_name: z.string().optional(),
  invited_by_profile_id: z.string(),
  invited_by_profile_name: z.string(),
  invited_at: z.string().datetime({ offset: true }),
});
const InviteStaff = _InviteCommon.and(NewInviteStaff);
const InviteCustomer = _InviteCommon
  .and(NewInviteCustomer)
  .and(z.object({ customer_name: z.string() }).partial());
const InviteApplicant = _InviteCommon
  .and(NewInviteApplicant)
  .and(
    z
      .object({
        customer_name: z.string(),
        job_role: z.string(),
        restart_of_application_id: z.string(),
        case_worker_name: z.string(),
        organisation_name: z.string(),
      })
      .partial()
  );
const Sort_GetInvites = z.enum([
  "type asc",
  "type desc",
  "active asc",
  "active desc",
  "accepted_at asc",
  "accepted_at desc",
  "email asc",
  "email desc",
  "customer_name asc",
  "customer_name desc",
  "job_role asc",
  "job_role desc",
  "invited_at asc",
  "invited_at desc",
  "invited_by_profile_name asc",
  "invited_by_profile_name desc",
]);
const ColumnFilters_GetInvites = z.array(
  z.union([
    z.object({
      id: z.literal("type"),
      value: z.enum(["staff", "customer", "applicant"]),
    }),
    z.object({ id: z.literal("employment_type"), value: EmploymentType }),
    z.object({ id: z.literal("active"), value: z.boolean() }),
    z.object({
      id: z.enum(["job_id", "case_worker_profile_id"]),
      value: z.string().uuid(),
    }),
    z.object({
      id: z.enum([
        "email",
        "customer_name",
        "job_role",
        "invited_by_profile_name",
      ]),
      value: z.string(),
    }),
    z.object({
      id: z.enum(["accepted_at", "invited_at"]),
      from: z.string().datetime({ offset: true }).optional(),
      to: z.string().datetime({ offset: true }).optional(),
    }),
  ])
);
const ReturnType_GetInvites = z.object({
  rows: z.array(z.union([InviteStaff, InviteCustomer, InviteApplicant])),
  meta: _PageableMeta,
  sort: Sort_GetInvites.optional(),
  globalFilter: z.string().optional(),
  columnFilters: ColumnFilters_GetInvites.optional(),
});
const createInvite_Body = z.union([
  NewInviteStaff,
  NewInviteCustomer,
  NewInviteApplicant,
]);
const columnFilters__3 = z
  .array(
    z.union([
      z.object({
        id: z.enum(["active", "allow_pay_by_invoice"]),
        value: z.boolean(),
      }),
      z.object({ id: z.literal("id"), value: z.array(z.string()) }),
      z.object({ id: z.enum(["name", "pin"]), value: z.string() }),
      z.object({
        id: z.enum(["created_at", "updated_at"]),
        from: z.string().datetime({ offset: true }).optional(),
        to: z.string().datetime({ offset: true }).optional(),
      }),
    ])
  )
  .optional();
const Customer = z.object({
  id: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  active: z.boolean(),
  name: z.string(),
  pin: z.string().optional(),
  telephone: z.string().optional(),
  allow_pay_by_invoice: z.boolean(),
  sector_healthcare: z.boolean(),
  enable_applicant_document_selector: z.boolean(),
  enable_applicant_payment: z.boolean(),
  automated_verification_trigger: z.enum(["off", "manual", "auto"]),
  customer_managed_case_workers: z.boolean(),
  allow_self_employed_applications: z.boolean(),
});
const Sort_GetCustomers = z.enum([
  "name asc",
  "name desc",
  "created_at asc",
  "created_at desc",
  "updated_at asc",
  "updated_at desc",
  "active asc",
  "active desc",
]);
const ColumnFilters_GetCustomers = z.array(
  z.union([
    z.object({
      id: z.enum(["active", "allow_pay_by_invoice"]),
      value: z.boolean(),
    }),
    z.object({ id: z.literal("id"), value: z.array(z.string()) }),
    z.object({ id: z.enum(["name", "pin"]), value: z.string() }),
    z.object({
      id: z.enum(["created_at", "updated_at"]),
      from: z.string().datetime({ offset: true }).optional(),
      to: z.string().datetime({ offset: true }).optional(),
    }),
  ])
);
const ReturnType_GetCustomers = z.object({
  rows: z.array(Customer),
  meta: _PageableMeta,
  sort: Sort_GetCustomers.optional(),
  globalFilter: z.string().optional(),
  columnFilters: ColumnFilters_GetCustomers.optional(),
});
const automated_verification_trigger = z.enum(["off", "manual", "auto"]);
const customer_managed_case_workers = z.boolean();
const allow_self_employed_applications = z.boolean();
const NewCustomer = z.object({
  name: z.string().min(1).max(60),
  pin: z.string().optional(),
  telephone: z.string().optional(),
  allow_pay_by_invoice: z.boolean(),
  sector_healthcare: z.boolean(),
  enable_applicant_document_selector: z.boolean(),
  enable_applicant_payment: z.boolean(),
  automated_verification_trigger: automated_verification_trigger,
  customer_managed_case_workers: customer_managed_case_workers.optional(),
  allow_self_employed_applications: allow_self_employed_applications.optional(),
});
const name = z.string();
const pin = z.string();
const telephone = z.string();
const allow_pay_by_invoice = z.boolean();
const sector_healthcare = z.boolean();
const enable_applicant_document_selector = z.boolean();
const enable_applicant_payment = z.boolean();
const UpdateCustomer = z
  .object({
    name: name,
    pin: pin,
    telephone: telephone,
    allow_pay_by_invoice: allow_pay_by_invoice,
    sector_healthcare: sector_healthcare,
    enable_applicant_document_selector: enable_applicant_document_selector,
    enable_applicant_payment: enable_applicant_payment,
    automated_verification_trigger: automated_verification_trigger,
    customer_managed_case_workers: customer_managed_case_workers,
    allow_self_employed_applications: allow_self_employed_applications,
  })
  .partial();
const ReturnType_GetCustomerExpanded = z.object({
  admin_profiles: z.array(
    z.object({
      id: z.string().uuid(),
      given_name: z.string(),
      family_name: z.string(),
    })
  ),
  customer: z.object({ id: z.string().uuid() }),
  applications: ApplicationStats,
});
const columnFilters__4 = z
  .array(
    z.union([
      z.object({
        id: z.literal("application_type"),
        value: z.enum(["standard", "enhanced"]),
      }),
      z.object({
        id: z.literal("application_workforce"),
        value: z.enum(["adult_only", "child_only", "child_and_adult", "other"]),
      }),
      z.object({
        id: z.enum([
          "active",
          "working_with_children",
          "working_with_adults",
          "working_at_home_address",
          "volunteer",
        ]),
        value: z.boolean(),
      }),
      z.object({ id: z.literal("id"), value: z.array(z.string()) }),
      z.object({ id: z.literal("customer_id"), value: z.string().uuid() }),
      z.object({ id: z.enum(["customer_name", "role"]), value: z.string() }),
      z.object({
        id: z.enum(["created_at", "updated_at"]),
        from: z.string().datetime({ offset: true }).optional(),
        to: z.string().datetime({ offset: true }).optional(),
      }),
    ])
  )
  .optional();
const GenericObject = z.object({
  id: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  active: z.boolean(),
});
const NewJob = z.object({
  customer_id: z.string().min(36).max(36),
  role: z.string().min(1).max(60),
  application_workforce: z.enum([
    "adult_only",
    "child_only",
    "child_and_adult",
    "other",
  ]),
  application_workforce_justification: z.string().optional(),
  application_type: z.enum(["standard", "enhanced"]),
  application_type_justification: z.string().optional(),
  working_with_children: z.boolean(),
  working_with_children_justification: z.string().optional(),
  working_with_adults: z.boolean(),
  working_with_adults_justification: z.string().optional(),
  working_at_home_address: z.boolean(),
  working_at_home_address_justification: z.string().optional(),
  volunteer: z.boolean(),
  with_adult_first: z.boolean(),
});
const Job = GenericObject.and(NewJob).and(
  z.object({ customer_name: z.string() }).partial()
);
const Sort_GetJobs = z.enum([
  "role asc",
  "role desc",
  "application_workforce asc",
  "application_workforce desc",
  "application_type asc",
  "application_type desc",
  "working_with_adults asc",
  "working_with_adults desc",
  "working_with_children asc",
  "working_with_children desc",
  "working_at_home_address asc",
  "working_at_home_address desc",
  "volunteer asc",
  "volunteer desc",
  "customer_name asc",
  "customer_name desc",
  "created_at asc",
  "created_at desc",
  "updated_at asc",
  "updated_at desc",
  "active asc",
  "active desc",
]);
const ColumnFilters_GetJobs = z.array(
  z.union([
    z.object({
      id: z.literal("application_type"),
      value: z.enum(["standard", "enhanced"]),
    }),
    z.object({
      id: z.literal("application_workforce"),
      value: z.enum(["adult_only", "child_only", "child_and_adult", "other"]),
    }),
    z.object({
      id: z.enum([
        "active",
        "working_with_children",
        "working_with_adults",
        "working_at_home_address",
        "volunteer",
      ]),
      value: z.boolean(),
    }),
    z.object({ id: z.literal("id"), value: z.array(z.string()) }),
    z.object({ id: z.literal("customer_id"), value: z.string().uuid() }),
    z.object({ id: z.enum(["customer_name", "role"]), value: z.string() }),
    z.object({
      id: z.enum(["created_at", "updated_at"]),
      from: z.string().datetime({ offset: true }).optional(),
      to: z.string().datetime({ offset: true }).optional(),
    }),
  ])
);
const ReturnType_GetJobs = z.object({
  rows: z.array(Job),
  meta: _PageableMeta,
  sort: Sort_GetJobs.optional(),
  globalFilter: z.string().optional(),
  columnFilters: ColumnFilters_GetJobs.optional(),
});
const UpdateJob = z.object({ role: z.string().min(1).max(60) });
const JSONPatchRequestAddReplaceTest = z.object({
  path: z.string(),
  value: z.unknown(),
  op: z.enum(["add", "replace", "test", "_get"]),
});
const JSONPatchRequestRemove = z.object({
  path: z.string(),
  op: z.literal("remove"),
});
const JSONPatchRequestMoveCopy = z.object({
  path: z.string(),
  op: z.enum(["move", "copy"]),
  from: z.string(),
});
const PatchRequest = z.array(
  z.union([
    JSONPatchRequestAddReplaceTest,
    JSONPatchRequestRemove,
    JSONPatchRequestMoveCopy,
  ])
);
const JobChange = z.object({
  changed_by_profile_id: z.string().uuid(),
  changed_by_profile_name: z.string().optional(),
  changed_at: z.string().datetime({ offset: true }),
  diff: PatchRequest,
});
const JobChangeLog = z.object({ changes: z.array(JobChange) }).partial();
const ReturnType_GetJobExpanded = z.object({
  job: z.object({
    id: z.string().uuid(),
    application_workforce_justification: z.string().optional(),
    application_type_justification: z.string().optional(),
    working_with_adults_justification: z.string().optional(),
    working_with_children_justification: z.string().optional(),
    working_at_home_address_justification: z.string().optional(),
    last_checked_at: z.string().datetime({ offset: true }).optional(),
    last_checked_by_profile_id: z.string().uuid().optional(),
    last_checked_by_profile_name: z.string().optional(),
    change_log: JobChangeLog.optional(),
  }),
  applications: ApplicationStats,
});
const EBulkApplicationPaymentMethod = z.enum([
  "none",
  "invoice",
  "paypal",
  "stripe",
  "external_stripe_paymentlink",
  "external_creditcard",
  "external_other",
]);
const columnFilters__5 = z
  .array(
    z.union([
      z.object({
        id: z.literal("status"),
        value: z.enum([
          "draft",
          "awaiting_customer_approval",
          "awaiting_automated_verification",
          "awaiting_payment",
          "needs_changes",
          "awaiting_countersigning",
          "awaiting_submission_to_DBS",
          "awaiting_response_from_DBS",
          "error_from_DBS",
          "rejected",
          "restarted",
          "in_progress",
          "completed",
          "withdrawn",
        ]),
      }),
      z.object({
        id: z.literal("payment_method"),
        value: EBulkApplicationPaymentMethod,
      }),
      z.object({ id: z.literal("employment_type"), value: EmploymentType }),
      z.object({
        id: z.enum([
          "active",
          "test_application",
          "payment_reference_IS_NULL",
          "countersigned_cs_number_IS_NULL",
          "dbs_enumber_IS_NULL",
          "dbs_isa_reference_IS_NULL",
          "documents_customer_IS_ADULT_FIRST",
          "external_invoice_reference_IS_NULL",
        ]),
        value: z.boolean(),
      }),
      z.object({ id: z.literal("id"), value: z.array(z.string()) }),
      z.object({
        id: z.enum(["customer_id", "job_id", "case_worker_profile_id"]),
        value: z.string().uuid(),
      }),
      z.object({
        id: z.enum([
          "customer_name",
          "job_role",
          "role",
          "payment_reference",
          "profile_email",
          "profile_given_name",
          "profile_family_name",
          "dbs_enumber",
          "dbs_isa_reference",
        ]),
        value: z.string(),
      }),
      z.object({
        id: z.enum([
          "created_at",
          "updated_at",
          "payment_at",
          "data.DateOfBirth",
        ]),
        from: z.string().datetime({ offset: true }).optional(),
        to: z.string().datetime({ offset: true }).optional(),
      }),
    ])
  )
  .optional();
const ConsentKind = z.enum([
  "fair_processing",
  "privacy_notice",
  "eresult",
  "review_privacy_notice",
]);
const ConsentScreen = z.enum(["consent_page", "review_page"]);
const ConsentRecord = z.object({
  kind: ConsentKind,
  screen: ConsentScreen,
  wording_version: z.string(),
  given_at: z.string().datetime({ offset: true }),
  given_by_profile_id: z.string().uuid().optional(),
  source: z.enum(["applicant", "backfill"]),
  note: z.string().optional(),
});
const ConsentLog = z
  .object({ records: z.array(ConsentRecord) })
  .partial()
  .passthrough();
const TitleType = z.enum([
  "BARON",
  "BARONESS",
  "BRIGADIER",
  "CANON",
  "CAPTAIN",
  "DR",
  "DUCHESS",
  "DUKE",
  "ESQ",
  "FATHER",
  "HON",
  "INSPECTOR",
  "LADY",
  "LORD",
  "LT COL",
  "MAJOR",
  "MISS",
  "MOST REVER",
  "MR",
  "MRS",
  "MS",
  "PASTOR",
  "PROFESSOR",
  "RABBI",
  "REV DR",
  "REVEREND",
  "RT REVEREN",
  "SIR",
  "SISTER",
  "SQUADRON L",
  "WG CDR",
]);
const ForenameType = z.string();
const MiddlenamesStructure = z
  .object({ Middlename: z.array(ForenameType).min(1).max(3) })
  .passthrough();
const SurnameType = z.string();
const ContemporaryDateType = z.string();
const GenderType = z.enum(["male", "female"]);
const NationalInsuranceNumberType = z.string();
const AddressLineType = z.string();
const ShortAddressLineType = z.string();
const PostCodeType = z.string();
const ISOCountryCodeType = z.enum([
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AN",
  "AO",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BM",
  "BN",
  "BO",
  "BR",
  "BS",
  "BT",
  "BV",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CX",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "EH",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "FX",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GS",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HM",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LX",
  "LY",
  "MA",
  "MC",
  "MD",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SH",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "ST",
  "SV",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TF",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TM",
  "TN",
  "TO",
  "TP",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "UM",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "YE",
  "YT",
  "YU",
  "ZA",
  "ZM",
  "ZR",
  "ZW",
]);
const AddressStructure = z
  .object({
    AddressLine1: AddressLineType.min(1)
      .max(60)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      ),
    AddressTown: ShortAddressLineType.min(1)
      .max(30)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      ),
    AddressCounty: ShortAddressLineType.min(1)
      .max(30)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      )
      .optional(),
    Postcode: PostCodeType.min(1)
      .max(30)
      .regex(
        /([A-Z0-9\(\)\-/']+)|([A-Z0-9\(\)\-/'][A-Z 0-9\(\)\-/']*[A-Z0-9\(\)\-/'])/
      )
      .optional(),
    CountryCode: ISOCountryCodeType,
    AddressLine2: AddressLineType.min(1)
      .max(60)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      )
      .optional(),
  })
  .passthrough();
const ContemporaryYearMonthType = z.string();
const CurrentAddressDateStructure = z
  .object({
    Address: AddressStructure,
    ResidentFromGyearMonth: ContemporaryYearMonthType.regex(/\d{4}-\d{2}/),
  })
  .passthrough();
const ResidentDateRangeStructure = z
  .object({
    ResidentFromGyearMonth: ContemporaryYearMonthType.regex(/\d{4}-\d{2}/),
    ResidentToGyearMonth: ContemporaryYearMonthType.regex(/\d{4}-\d{2}/),
  })
  .passthrough();
const PreviousAddressDateStructure = z
  .object({
    Address: AddressStructure,
    ResidentDates: ResidentDateRangeStructure,
  })
  .passthrough();
const ContemporaryYearType = z.string();
const PhoneNumberType = z.string();
const YesNoType = z.enum(["y", "n"]);
const LanguagePreferenceType = z.enum(["english", "welsh"]);
const OtherNamesStructure = z
  .object({
    Name: ForenameType.min(1)
      .max(60)
      .regex(/([A-Z]+)|([A-Z][A-Z '\-]*[A-Z])/),
    UsedFrom: ContemporaryYearType.regex(/\d{4}/),
    UsedTo: ContemporaryYearType.regex(/\d{4}/).optional(),
  })
  .passthrough();
const OtherForenames = z
  .object({ OtherForename: z.array(OtherNamesStructure).min(1).max(200) })
  .passthrough();
const OtherSurnames = z
  .object({ OtherSurname: z.array(OtherNamesStructure).min(1).max(200) })
  .passthrough();
const ISOCountryDescType = z.string();
const CheckedByType = z.string();
const PassportNumberType = z.string();
const PassportStructure = z
  .object({
    PassportNumber: PassportNumberType.min(1)
      .max(11)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      ),
    PassportDob: ContemporaryDateType.regex(/\d{4}-\d{2}-\d{2}/),
    PassportNationality: ISOCountryDescType.min(1)
      .max(30)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      ),
    PassportIssueDate: ContemporaryDateType.regex(/\d{4}-\d{2}-\d{2}/),
  })
  .passthrough();
const DriverLicenceNumberType = z.string();
const DriverLicenceTypeType = z.enum(["paper", "photo"]);
const DriverLicenceStructure = z
  .object({
    DriverLicenceNumber: DriverLicenceNumberType.min(1)
      .max(18)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      ),
    DriverLicenceDOB: ContemporaryDateType.regex(/\d{4}-\d{2}-\d{2}/),
    DriverLicenceType: DriverLicenceTypeType,
    DriverLicenceValidFrom: ContemporaryDateType.regex(/\d{4}-\d{2}-\d{2}/),
    DriverLicenceIssueCountry: ISOCountryCodeType,
  })
  .passthrough();
const PartialEBulkApplicationData = z
  .object({
    NonApplicationFields: z
      .object({ NoPassportOrDriverLicence: z.boolean() })
      .partial(),
    Title: TitleType,
    Forename: ForenameType.min(1)
      .max(60)
      .regex(/([A-Z]+)|([A-Z][A-Z '\-]*[A-Z])/),
    Middlenames: MiddlenamesStructure,
    PresentSurname: SurnameType.min(1)
      .max(60)
      .regex(/([A-Z]+)|([A-Z][A-Z '\-]*[A-Z])/),
    DateOfBirth: ContemporaryDateType.regex(/\d{4}-\d{2}-\d{2}/),
    Gender: GenderType,
    NiNumber: NationalInsuranceNumberType.min(9)
      .max(9)
      .regex(
        /((([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*)|(([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*)|([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*)))|((([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*)|([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*))|([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*)))/
      ),
    CurrentAddress: CurrentAddressDateStructure,
    _PreviousAddress_DO_NOT_USE: PreviousAddressDateStructure,
    PreviousAddress: z.array(PreviousAddressDateStructure).max(200),
    AdditionalApplicantDetails: z
      .object({
        BirthSurname: SurnameType.min(1)
          .max(60)
          .regex(/([A-Z]+)|([A-Z][A-Z '\-]*[A-Z])/),
        BirthSurnameUntil: ContemporaryYearType.regex(/\d{4}/),
        BirthTown: ShortAddressLineType.min(1)
          .max(30)
          .regex(
            /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
          ),
        BirthCounty: ShortAddressLineType.min(1)
          .max(30)
          .regex(
            /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
          ),
        BirthCountry: ISOCountryCodeType,
        ContactNumber: PhoneNumberType.min(1)
          .max(30)
          .regex(
            /([A-Z0-9\(\)\-/&]+)|([A-Z0-9\(\)\-/&][A-Z 0-9\(\)\-/&]*[A-Z0-9\(\)\-/&])/
          ),
        UnspentConvictions: YesNoType,
        DeclarationByApplicant: YesNoType,
        LanguagePreference: LanguagePreferenceType,
        OtherForenames: OtherForenames,
        OtherSurnames: OtherSurnames,
        BirthNationality: ISOCountryDescType.min(1)
          .max(30)
          .regex(
            /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
          ),
      })
      .partial(),
    ApplicantIdentityDetails: z
      .object({
        IdentityVerified: YesNoType,
        EvidenceCheckedBy: CheckedByType.min(1)
          .max(60)
          .regex(
            /((([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-'])|(([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-'])|([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-'])))|((([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-'])|([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-']))|([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-'])))/
          ),
        PassportDetails: PassportStructure,
        DriverLicenceDetails: DriverLicenceStructure,
      })
      .partial(),
  })
  .partial();
const EBulkApplicationDocument = z.object({
  selected: z.boolean(),
  verified: z.boolean().optional(),
});
const EBulkApplicationDocuments = z
  .object({
    group1: z
      .object({
        passport: EBulkApplicationDocument,
        driverLicence: EBulkApplicationDocument,
        eVisa: EBulkApplicationDocument,
        birthCertificate: EBulkApplicationDocument,
        biometricResidencePermit: EBulkApplicationDocument,
        adoptionCertificate: EBulkApplicationDocument,
      })
      .partial(),
    group2a: z
      .object({
        driverLicenceNonEEA: EBulkApplicationDocument,
        driverLicencePaper: EBulkApplicationDocument,
        birthCertificate: EBulkApplicationDocument,
        marriageCivilPartnershipCertificate: EBulkApplicationDocument,
        hmForcesIdCard: EBulkApplicationDocument,
        validFirearmsLicence: EBulkApplicationDocument,
        immigrationDocumentVisaWorkPermit: EBulkApplicationDocument,
      })
      .partial(),
    group2b_last3mo: z
      .object({
        bankStatementUKCI: EBulkApplicationDocument,
        bankStatementUKCI_NonEEA: EBulkApplicationDocument,
        bankStatementOther: EBulkApplicationDocument,
        bankOpeningConfirmationLetter: EBulkApplicationDocument,
        creditCardStatement: EBulkApplicationDocument,
        utilityBill: EBulkApplicationDocument,
        benefitStatement: EBulkApplicationDocument,
        governmentDocumentGivingEntitlement: EBulkApplicationDocument,
        letterSponsorshipFutureEmployer: EBulkApplicationDocument,
      })
      .partial(),
    group2b_last12mo: z
      .object({
        mortgageStatement: EBulkApplicationDocument,
        financialStatement: EBulkApplicationDocument,
        p45_p60: EBulkApplicationDocument,
        councilTaxStatement: EBulkApplicationDocument,
      })
      .partial(),
    group2b_anyTime: z
      .object({
        euNationalIdentityCard: EBulkApplicationDocument,
        irishPassportCard: EBulkApplicationDocument,
        letterHeadteacher: EBulkApplicationDocument,
        passLogoCard: EBulkApplicationDocument,
      })
      .partial(),
    confirmations: z
      .object({
        currentAddressCount: z.number().int(),
        dateOfBirthCount: z.number().int(),
        nameAndNameChangesCount: z.number().int(),
      })
      .partial(),
    application: z.object({ withAdultFirst: z.boolean() }).partial(),
  })
  .partial();
const DBSTrackingStage = z.object({
  n: z.number().int(),
  name: z.string(),
  state: z.enum(["completed", "in_progress", "not_started", "not_required"]),
  completed_on: z.string().nullable(),
});
const DBSTrackingEntry = z.object({
  type: z.enum([
    "stages",
    "withdrawn",
    "dispatched",
    "not_found",
    "unrecognised",
  ]),
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  received_on: z.string().optional(),
  event_date: z.string().optional(),
  current_stage: z.number().int().optional(),
  stages: z.array(DBSTrackingStage).optional(),
  snippet: z.string().optional(),
});
const DBSTracking = z.object({
  latest: DBSTrackingEntry,
  history: z.array(DBSTrackingEntry),
});
const EBulkApplicationInternalNote = z.object({
  note: z.string(),
  saved_by_profile_id: z.string().uuid(),
  saved_at: z.string().datetime({ offset: true }),
});
const EBulkApplication = z.object({
  id: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  active: z.boolean(),
  restart_of_application_id: z.string().optional(),
  test_application: z.boolean().optional(),
  job_id: z.string().uuid().optional(),
  job_role: z.string().optional(),
  job_application_type: z.enum(["standard", "enhanced"]).optional(),
  job_volunteer: z.boolean().optional(),
  customer_id: z.string().uuid().optional(),
  customer_name: z.string().optional(),
  profile_email: z.string().optional(),
  profile_given_name: z.string().optional(),
  profile_family_name: z.string().optional(),
  case_worker_profile_id: z.string().uuid().optional(),
  case_worker_name: z.string().optional(),
  employment_type: EmploymentType.optional(),
  organisation_name: z.string().optional(),
  gdpr_consent: z.boolean().optional(),
  consent_log: ConsentLog.optional(),
  data: PartialEBulkApplicationData,
  status: z.enum([
    "draft",
    "awaiting_customer_approval",
    "awaiting_automated_verification",
    "awaiting_payment",
    "needs_changes",
    "awaiting_countersigning",
    "awaiting_submission_to_DBS",
    "awaiting_response_from_DBS",
    "error_from_DBS",
    "rejected",
    "restarted",
    "in_progress",
    "completed",
    "withdrawn",
  ]),
  rejection_reason: z.string().optional(),
  rejected_by: z.enum(["applicant", "customer", "staff"]).optional(),
  payment_at: z.string().datetime({ offset: true }).optional(),
  payment_method: EBulkApplicationPaymentMethod.optional(),
  payment_reference: z.string().optional(),
  payment_code: z.string().optional(),
  payment_pence: z.number().int().optional(),
  external_invoice_reference: z.string().optional(),
  documents_applicant: EBulkApplicationDocuments.optional(),
  documents_customer: EBulkApplicationDocuments.optional(),
  can_applicant_select_documents: z.boolean().optional(),
  can_applicant_pay: z.boolean().optional(),
  evidence_checked_by_profile_id: z.string().uuid().optional(),
  evidence_checked_at: z.string().datetime({ offset: true }).optional(),
  evidence_checked_by_name: z.string().optional(),
  evidence_checked_by_email: z.string().optional(),
  dbs_enumber: z.string().optional(),
  dbs_isa_reference: z.string().optional(),
  dbs_eresult: z.object({}).partial().passthrough().optional(),
  dbs_tracking: DBSTracking.optional(),
  internal_note: EBulkApplicationInternalNote.optional(),
});
const Sort_GetEBulkApplications = z.enum([
  "created_at asc",
  "created_at desc",
  "updated_at asc",
  "updated_at desc",
  "active asc",
  "active desc",
  "status asc",
  "status desc",
  "customer_name asc",
  "customer_name desc",
  "job_role asc",
  "job_role desc",
  "profile_given_name asc",
  "profile_given_name desc",
  "profile_family_name asc",
  "profile_family_name desc",
  "payment_at asc",
  "payment_at desc",
  "payment_method asc",
  "payment_method desc",
  "payment_reference asc",
  "payment_reference desc",
  "gdpr_consent asc",
  "gdpr_consent desc",
]);
const ColumnFilters_GetEBulkApplications = z.array(
  z.union([
    z.object({
      id: z.literal("status"),
      value: z.enum([
        "draft",
        "awaiting_customer_approval",
        "awaiting_automated_verification",
        "awaiting_payment",
        "needs_changes",
        "awaiting_countersigning",
        "awaiting_submission_to_DBS",
        "awaiting_response_from_DBS",
        "error_from_DBS",
        "rejected",
        "restarted",
        "in_progress",
        "completed",
        "withdrawn",
      ]),
    }),
    z.object({
      id: z.literal("payment_method"),
      value: EBulkApplicationPaymentMethod,
    }),
    z.object({ id: z.literal("employment_type"), value: EmploymentType }),
    z.object({
      id: z.enum([
        "active",
        "test_application",
        "payment_reference_IS_NULL",
        "countersigned_cs_number_IS_NULL",
        "dbs_enumber_IS_NULL",
        "dbs_isa_reference_IS_NULL",
        "documents_customer_IS_ADULT_FIRST",
        "external_invoice_reference_IS_NULL",
      ]),
      value: z.boolean(),
    }),
    z.object({ id: z.literal("id"), value: z.array(z.string()) }),
    z.object({
      id: z.enum(["customer_id", "job_id", "case_worker_profile_id"]),
      value: z.string().uuid(),
    }),
    z.object({
      id: z.enum([
        "customer_name",
        "job_role",
        "role",
        "payment_reference",
        "profile_email",
        "profile_given_name",
        "profile_family_name",
        "dbs_enumber",
        "dbs_isa_reference",
      ]),
      value: z.string(),
    }),
    z.object({
      id: z.enum([
        "created_at",
        "updated_at",
        "payment_at",
        "data.DateOfBirth",
      ]),
      from: z.string().datetime({ offset: true }).optional(),
      to: z.string().datetime({ offset: true }).optional(),
    }),
  ])
);
const ReturnType_GetEBulkApplications = z.object({
  rows: z.array(EBulkApplication),
  meta: _PageableMeta,
  sort: Sort_GetEBulkApplications.optional(),
  globalFilter: z.string().optional(),
  columnFilters: ColumnFilters_GetEBulkApplications.optional(),
});
const ReturnType_GetUninvoicedApplications = z.object({
  uninvoicedApplicationIds: z.array(z.string()),
});
const PostBody_MarkApplicationsAsInvoiced = z.object({
  application_ids: z.array(z.string()),
  external_invoice_reference: z.string(),
});
const x_dbs_api_clear_fields = z
  .union([z.string(), z.array(z.string())])
  .optional();
const AdditionalApplicantDetailsStructure = z
  .object({
    BirthSurname: SurnameType.min(1)
      .max(60)
      .regex(/([A-Z]+)|([A-Z][A-Z '\-]*[A-Z])/)
      .optional(),
    BirthSurnameUntil: ContemporaryYearType.regex(/\d{4}/).optional(),
    BirthTown: ShortAddressLineType.min(1)
      .max(30)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      ),
    BirthCounty: ShortAddressLineType.min(1)
      .max(30)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      )
      .optional(),
    BirthCountry: ISOCountryCodeType,
    ContactNumber: PhoneNumberType.min(1)
      .max(30)
      .regex(
        /([A-Z0-9\(\)\-/&]+)|([A-Z0-9\(\)\-/&][A-Z 0-9\(\)\-/&]*[A-Z0-9\(\)\-/&])/
      )
      .optional(),
    UnspentConvictions: YesNoType,
    DeclarationByApplicant: YesNoType,
    LanguagePreference: LanguagePreferenceType,
    OtherForenames: z
      .object({ OtherForename: z.array(OtherNamesStructure).min(1).max(200) })
      .passthrough()
      .optional(),
    OtherSurnames: z
      .object({ OtherSurname: z.array(OtherNamesStructure).min(1).max(200) })
      .passthrough()
      .optional(),
    BirthNationality: ISOCountryDescType.min(1)
      .max(30)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      )
      .optional(),
  })
  .passthrough();
const ApplicantIdentityDetailsStructure = z
  .object({
    IdentityVerified: YesNoType,
    EvidenceCheckedBy: CheckedByType.min(1)
      .max(60)
      .regex(
        /((([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-'])|(([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-'])|([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-'])))|((([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-'])|([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-']))|([A-Z0-9\-']+)|([A-Z0-9\-'][A-Z 0-9\-']*[A-Z0-9\-'])))/
      ),
    PassportDetails: PassportStructure.optional(),
    DriverLicenceDetails: DriverLicenceStructure.optional(),
  })
  .passthrough();
const ApplicantDetailsStructure = z
  .object({
    Title: TitleType,
    Middlenames: MiddlenamesStructure.optional(),
    PresentSurname: SurnameType.min(1)
      .max(60)
      .regex(/([A-Z]+)|([A-Z][A-Z '\-]*[A-Z])/),
    CurrentAddress: CurrentAddressDateStructure,
    PreviousAddress: z.array(PreviousAddressDateStructure).max(200).optional(),
    DateOfBirth: ContemporaryDateType.regex(/\d{4}-\d{2}-\d{2}/),
    Gender: GenderType,
    NINumber: NationalInsuranceNumberType.min(9)
      .max(9)
      .regex(
        /((([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*)|(([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*)|([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*)))|((([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*)|([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*))|([A-Z0-9]+)|([A-Z0-9][A-Z 0-9]*)))/
      )
      .optional(),
    AdditionalApplicantDetails: AdditionalApplicantDetailsStructure,
    ApplicantIdentityDetails: ApplicantIdentityDetailsStructure,
    Forename: ForenameType.min(1)
      .max(60)
      .regex(/([A-Z]+)|([A-Z][A-Z '\-]*[A-Z])/),
  })
  .passthrough();
const EBulkApplicationIssue = z.object({
  ruleId: z.string(),
  field: z.string(),
  message: z.string(),
  severity: z.enum(["error", "warning"]),
});
const EBulkApplicationGateRefused = z.object({
  error: z.object({
    type: z.literal("errEBulkApplicationInvalid"),
    message: z.string().optional(),
  }),
  issues: z.array(EBulkApplicationIssue),
});
const status = z.enum([
  "draft",
  "awaiting_customer_approval",
  "awaiting_automated_verification",
  "awaiting_payment",
  "needs_changes",
  "awaiting_countersigning",
  "awaiting_submission_to_DBS",
  "awaiting_response_from_DBS",
  "error_from_DBS",
  "rejected",
  "restarted",
  "in_progress",
  "completed",
  "withdrawn",
]);
const Price = z.object({
  code: z.enum([
    "standard",
    "enhanced",
    "enhanced_with_adult_first",
    "volunteer",
    "test",
  ]),
  priceInPence: z.number(),
  label: z.string(),
  stripePriceId: z.string(),
});
const ReturnType_GetEBulkApplicationPrice = z.object({
  applicationStatus: status,
  price: Price,
  allowedPaymentMethods: z.array(EBulkApplicationPaymentMethod),
  alreadyPaidInPence: z.number(),
  amountToPayInPence: z.number(),
});
const StripeCheckout = z
  .object({
    id: z.string(),
    payment_intent: z
      .union([
        z
          .object({ id: z.string(), object: z.literal("payment_intent") })
          .passthrough(),
        z.string(),
      ])
      .nullish(),
    payment_status: z.enum(["paid", "unpaid", "no_payment_required"]).nullish(),
    amount_total: z.number().nullish(),
    expires_at: z.number(),
    client_secret: z.string().nullish(),
  })
  .passthrough();
const StripeCheckoutsExisting = z
  .object({ checkouts: z.array(StripeCheckout) })
  .passthrough();
const StripeStatus_NoChange = z
  .object({
    application: z.literal("application_not_awaiting_payment").optional(),
  })
  .passthrough();
const StripeStatus_Updated = z
  .object({
    application: z.literal("marked_as_paid").optional(),
    status: z.enum(["complete", "expired", "open"]).optional(),
    payment_status: z.enum(["paid", "unpaid", "no_payment_required"]),
  })
  .passthrough();
const StripeStatus = z.union([StripeStatus_NoChange, StripeStatus_Updated]);
const EBulkApplicationPayment = z.union([
  z.object({ payment_method: z.literal("none") }),
  z.object({
    payment_method: z.literal("invoice"),
    payment_pence: z.number().int(),
  }),
  z.object({
    payment_method: z.literal("paypal"),
    payment_pence: z.number().int(),
    payment_reference: z.string(),
  }),
  z.object({
    payment_method: z.literal("stripe"),
    payment_pence: z.number().int(),
    payment_reference: z.string(),
  }),
  z.object({
    payment_method: z.literal("external_stripe_paymentlink"),
    payment_pence: z.number().int(),
    payment_reference: z.string(),
  }),
  z.object({
    payment_method: z.literal("external_creditcard"),
    payment_pence: z.number().int(),
    payment_reference: z.string(),
  }),
  z.object({
    payment_method: z.literal("external_other"),
    payment_pence: z.number().int(),
    payment_reference: z.string(),
  }),
]);
const EBulkApplicationCorrection = z.object({
  corrected_by_profile_id: z.string().uuid(),
  corrected_at: z.string().datetime({ offset: true }),
  diff: PatchRequest,
  acknowledged_issues: z.array(EBulkApplicationIssue).optional(),
});
const EBulkApplicationCorrectionLog = z
  .object({ corrections: z.array(EBulkApplicationCorrection) })
  .partial();
const PatchSet = z.object({
  diff: PatchRequest,
  acknowledge: z.boolean().optional(),
});
const EBulkApplicationCorrectionRefused = z.object({
  error: z.object({
    type: z.literal("errEBulkApplicationInvalid"),
    message: z.string().optional(),
  }),
  issues: z.array(EBulkApplicationIssue),
});
const EBulkApplicationValidation = z.object({
  gate: z.literal("countersign"),
  issues: z.array(EBulkApplicationIssue),
});
const EBulkApplicationTransitionLog = z
  .object({
    changes: z.array(
      z.object({
        transitioned_at: z.string().datetime({ offset: true }),
        transitioned_by_profile_id: z.string(),
        transitioned_from: z.string(),
        transitioned_to: z.string(),
        transitioned_metadata: z.object({}).partial().passthrough().optional(),
      })
    ),
  })
  .partial();
const SetISAReferenceEBulkApplication = z.object({ isa_reference: z.string() });
const SetInternalNoteEBulkApplication = z.object({ internal_note: z.string() });
const RejectEBulkApplication = z.union([
  z.object({ reason: z.string(), by: z.literal("applicant") }),
  z.object({
    reason: z.string(),
    by: z.literal("customer"),
    notify_applicant: z.boolean(),
  }),
  z.object({
    reason: z.string(),
    by: z.literal("staff"),
    notify_applicant: z.boolean(),
    notify_customer: z.boolean(),
  }),
]);
const PostBody_EBulkApplicationMarkAsError = z.object({
  batch_id: z.string().uuid(),
  error_code: z.string(),
  error_reason: z.string(),
});
const Pricing = z.object({
  mode: z.enum(["live", "test"]),
  prices: z.array(Price),
});
const PositionAppliedForType = z.string();
const OrganisationNameType = z.string();
const EmploymentDetailsStructure = z
  .object({
    PositionAppliedFor: PositionAppliedForType.min(1)
      .max(60)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      ),
    OrganisationName: OrganisationNameType.min(1)
      .max(60)
      .regex(
        /([A-Z0-9\(\)\-/'&]+)|([A-Z0-9\(\)\-/'&][A-Z 0-9\(\)\-/'&]*[A-Z0-9\(\)\-/'&])/
      ),
  })
  .passthrough();
const RbNoType = z.string();
const CsNoType = z.string();
const DisclosureTypeType = z.enum(["standard", "enhanced"]);
const RBApplicationReferenceType = z.string();
const RBdetailsStructure = z
  .object({
    RBNumber: RbNoType.min(11)
      .max(11)
      .regex(/\d{11}/),
    CSigNumber: CsNoType.min(11)
      .max(11)
      .regex(/\d{11}/),
    DisclosureType: DisclosureTypeType,
    WorkingWithVulnerableAdults: YesNoType,
    WorkingWithChildren: YesNoType,
    CurrentAddressDetailsChecked: YesNoType,
    Volunteer: YesNoType,
    WorkingAtHomeAddress: YesNoType,
    RBApplicationReference: RBApplicationReferenceType.min(1)
      .max(30)
      .regex(/[A-Za-z 0-9_\(\)\\\-/'"&]{4,30}x]/),
  })
  .passthrough();
const eBulkApplicationStructure = z
  .object({
    ApplicantDetails: ApplicantDetailsStructure,
    PotentialEmployerDetails: EmploymentDetailsStructure,
    RBdetails: RBdetailsStructure,
  })
  .passthrough();
const columnFilters__6 = z
  .array(
    z.union([
      z.object({
        id: z.literal("batch_message_type"),
        value: z.enum(["CRB01", "CRB02", "CRB03", "CRB04"]),
      }),
      z.object({
        id: z.literal("result_type"),
        value: z.enum(["success", "error"]),
      }),
      z.object({ id: z.literal("active"), value: z.boolean() }),
      z.object({ id: z.literal("id"), value: z.array(z.string()) }),
      z.object({ id: z.literal("application_ids"), value: z.string().uuid() }),
      z.object({ id: z.literal("batch_filename"), value: z.string() }),
      z.object({
        id: z.enum(["created_at", "updated_at", "transferred_at"]),
        from: z.string().datetime({ offset: true }).optional(),
        to: z.string().datetime({ offset: true }).optional(),
      }),
    ])
  )
  .optional();
const EBulkApplicationsBatch = z.object({
  id: z.string().uuid(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  active: z.boolean(),
  batch_number: z.number().int().optional(),
  batch_message_type: z.enum(["CRB01", "CRB02", "CRB03", "CRB04"]),
  batch_xml: z.string(),
  batch_filename: z.string(),
  application_ids: z.array(z.string()),
  transferred_at: z.string().datetime({ offset: true }).optional(),
  result_type: z.enum(["success", "error"]).optional(),
  log_data: z.object({}).partial().passthrough().optional(),
  processing_started_at: z.string().datetime({ offset: true }).optional(),
  processing_ended_at: z.string().datetime({ offset: true }).optional(),
});
const Sort_GetEBulkApplicationsBatches = z.enum([
  "created_at asc",
  "created_at desc",
  "updated_at asc",
  "updated_at desc",
  "active asc",
  "active desc",
  "batch_number asc",
  "batch_number desc",
  "batch_message_type asc",
  "batch_message_type desc",
  "batch_filename asc",
  "batch_filename desc",
  "transferred_at asc",
  "transferred_at desc",
  "result_type asc",
  "result_type desc",
]);
const ColumnFilters_GetEBulkApplicationsBatches = z.array(
  z.union([
    z.object({
      id: z.literal("batch_message_type"),
      value: z.enum(["CRB01", "CRB02", "CRB03", "CRB04"]),
    }),
    z.object({
      id: z.literal("result_type"),
      value: z.enum(["success", "error"]),
    }),
    z.object({ id: z.literal("active"), value: z.boolean() }),
    z.object({ id: z.literal("id"), value: z.array(z.string()) }),
    z.object({ id: z.literal("application_ids"), value: z.string().uuid() }),
    z.object({ id: z.literal("batch_filename"), value: z.string() }),
    z.object({
      id: z.enum(["created_at", "updated_at", "transferred_at"]),
      from: z.string().datetime({ offset: true }).optional(),
      to: z.string().datetime({ offset: true }).optional(),
    }),
  ])
);
const ReturnType_GetEBulkApplicationsBatches = z.object({
  rows: z.array(EBulkApplicationsBatch),
  meta: _PageableMeta,
  sort: Sort_GetEBulkApplicationsBatches.optional(),
  globalFilter: z.string().optional(),
  columnFilters: ColumnFilters_GetEBulkApplicationsBatches.optional(),
});
const EBulkBatchLeftOut = z.object({
  id: z.string().uuid(),
  issues: z.array(EBulkApplicationIssue),
});
const PostBody_EBulkApplicationsBatchSync = z
  .object({
    CRB01: z.boolean(),
    CRB02: z.boolean(),
    CRB03: z.boolean(),
    CRB04: z.boolean(),
  })
  .partial();
const CRBResponse = z.object({
  batchIds: z.array(z.string()),
  leftOut: z.array(EBulkBatchLeftOut).optional(),
});
const ReturnType_EBulkApplicationsBatchSync = z
  .object({
    CRB01: CRBResponse,
    CRB02: CRBResponse,
    CRB03: CRBResponse,
    CRB04: CRBResponse,
  })
  .partial();
const EBulkCertificateStatus = z.enum(["ok", "warning", "critical", "missing"]);
const ReturnType_GetEBulkClientCertificate = z.object({
  configured: z.boolean(),
  status: EBulkCertificateStatus,
  checked_at: z.string().datetime({ offset: true }),
  subject: z.string().optional(),
  issuer: z.string().optional(),
  serial: z.string().optional(),
  valid_from: z.string().datetime({ offset: true }).optional(),
  valid_to: z.string().datetime({ offset: true }).optional(),
  days_remaining: z.number().int().optional(),
  ca_bundle: z
    .object({
      count: z.number().int(),
      earliest_valid_to: z.string().datetime({ offset: true }),
      days_remaining: z.number().int(),
      status: EBulkCertificateStatus,
    })
    .optional(),
});
const PostBody_EBulkApplicationsBatchValidate = z.object({
  batch_id: z.string().uuid(),
});
const PostBody_StripeReconcile = z.object({
  update_checkout_sessions: z.boolean(),
  check_payment_status: z.boolean(),
  action_updates: z.boolean(),
});
const PostBody_TestMoveEBulkApplication = z.object({
  targetApplicantProfileId: z.string().uuid(),
  targetCustomerProfileId: z.string().uuid(),
});
const PostBody_TestCreateEBulkApplicationsBatchCRB02 = z.object({
  batchId: z.string(),
  errorCode: z.enum([
    "001",
    "002",
    "003",
    "004",
    "005",
    "006",
    "007",
    "008",
    "009",
    "010",
    "011",
  ]),
});
const PostBody_TestCreateEBulkApplicationsBatchCRB03 = z.object({
  applications: z.array(
    z.union([
      z.object({
        applicationId: z.string().uuid(),
        applicationStatus: z.literal("OK"),
        applicationFormReference: z.string(),
      }),
      z.object({
        applicationId: z.string().uuid(),
        applicationStatus: z.literal("ERROR"),
        errorCode: z.enum([
          "101",
          "102",
          "103",
          "104",
          "105",
          "106",
          "107",
          "108",
          "109",
          "110",
          "111",
          "112",
          "113",
          "114",
          "115",
          "116",
          "117",
          "118",
          "119",
          "120",
          "121",
          "122",
          "123",
          "124",
          "125",
        ]),
      }),
    ])
  ),
});
const PostBody_TestCreateEBulkApplicationsBatchCRB04 = z.object({
  applications: z.array(
    z.object({
      applicationId: z.string().uuid(),
      disclosureStatus: z.enum(["clean", "wait"]),
      disclosureNumber: z.string(),
      disclosureIssueDate: z.string(),
    })
  ),
});
const DBSTrackingSweepState = z.object({
  running: z.boolean(),
  aborted: z.boolean(),
  started_at: z.string(),
  finished_at: z.string().optional(),
  total: z.number().int(),
  totals: z.object({
    swept: z.number().int(),
    changed: z.number().int(),
    unchanged: z.number().int(),
    transient: z.number().int(),
    failed: z.number().int(),
    withdrawn_seen: z.number().int(),
    skipped_fresh: z.number().int(),
  }),
  failures: z.array(z.object({ dbs_enumber: z.string(), error: z.string() })),
});
const reconcileEBulkApplicationTracking_Body = z
  .object({
    confirm: z.boolean(),
    buckets: z.array(
      z.enum([
        "withdrawn",
        "dispatched_under_14",
        "dispatched_14_to_28",
        "dispatched_over_28",
      ])
    ),
  })
  .partial();
const AnalyticsBandCounts = z.object({
  total: z.number().int(),
  standard: z.number().int(),
  enhanced: z.number().int(),
  enhancedWithAdultFirst: z.number().int(),
  volunteer: z.number().int(),
  unclassified: z.number().int(),
});
const ReturnType_GetAnalyticsStats = z.object({
  monthly: z.array(
    z.object({
      month: z.string(),
      created: AnalyticsBandCounts,
      accepted: z.object({
        total: z.number().int(),
        standard: z.number().int(),
        enhanced: z.number().int(),
        enhancedWithAdultFirst: z.number().int(),
        volunteer: z.number().int(),
        unclassified: z.number().int(),
        adultFirst: z.number().int(),
      }),
      revenue: z.object({
        paidApplications: z.number().int(),
        immediatePence: z.number().int(),
        invoicePence: z.number().int(),
        totalPence: z.number().int(),
      }),
      dbsCostPence: z.number().int(),
      activeCustomers: z.number().int(),
    })
  ),
  daily: z.array(
    z.object({
      day: z.string(),
      created: z.number().int(),
      accepted: z.number().int(),
    })
  ),
  bill: z.array(
    z.object({
      month: z.string(),
      acceptedTotal: z.number().int(),
      lines: z.array(
        z.object({
          key: z.enum([
            "standard",
            "enhanced",
            "volunteer",
            "adult_first",
            "unclassified",
          ]),
          label: z.string(),
          count: z.number().int(),
          feePence: z.number().int(),
          subtotalPence: z.number().int(),
        })
      ),
      totalPence: z.number().int(),
      projectedPence: z.number().int().optional(),
      lastYear: z
        .object({
          month: z.string(),
          acceptedTotal: z.number().int(),
          totalPence: z.number().int(),
        })
        .optional(),
    })
  ),
  feeSchedule: z.array(
    z.object({
      effectiveFrom: z.string(),
      standardPence: z.number().int(),
      enhancedPence: z.number().int(),
      volunteerPence: z.number().int(),
      adultFirstPence: z.number().int(),
    })
  ),
});
const RecordEBulkApplicationConsents = z.object({
  screen: ConsentScreen,
  wording_version: z.string(),
  kinds: z.array(ConsentKind).min(1),
});

export const schemas = {
  columnFilters,
  ProfileDataEmailPreferences,
  ProfileData,
  Profile,
  _PageableMeta,
  Sort_GetProfiles,
  ColumnFilters_GetProfiles,
  ReturnType_GetProfiles,
  ErrorResponse,
  ProfileResponse,
  createMyProfile_Body,
  ApplicationStats,
  ReturnType_GetGlobalStats,
  ReturnType_GetCurrentUsageStats,
  NewInviteStaff,
  MyInviteStaff,
  NewInviteCustomer,
  MyInviteCustomer,
  EmploymentType,
  NewInviteApplicant,
  MyInviteApplicant,
  SetCaseWorker,
  columnFilters__2,
  _InviteCommon,
  InviteStaff,
  InviteCustomer,
  InviteApplicant,
  Sort_GetInvites,
  ColumnFilters_GetInvites,
  ReturnType_GetInvites,
  createInvite_Body,
  columnFilters__3,
  Customer,
  Sort_GetCustomers,
  ColumnFilters_GetCustomers,
  ReturnType_GetCustomers,
  automated_verification_trigger,
  customer_managed_case_workers,
  allow_self_employed_applications,
  NewCustomer,
  name,
  pin,
  telephone,
  allow_pay_by_invoice,
  sector_healthcare,
  enable_applicant_document_selector,
  enable_applicant_payment,
  UpdateCustomer,
  ReturnType_GetCustomerExpanded,
  columnFilters__4,
  GenericObject,
  NewJob,
  Job,
  Sort_GetJobs,
  ColumnFilters_GetJobs,
  ReturnType_GetJobs,
  UpdateJob,
  JSONPatchRequestAddReplaceTest,
  JSONPatchRequestRemove,
  JSONPatchRequestMoveCopy,
  PatchRequest,
  JobChange,
  JobChangeLog,
  ReturnType_GetJobExpanded,
  EBulkApplicationPaymentMethod,
  columnFilters__5,
  ConsentKind,
  ConsentScreen,
  ConsentRecord,
  ConsentLog,
  TitleType,
  ForenameType,
  MiddlenamesStructure,
  SurnameType,
  ContemporaryDateType,
  GenderType,
  NationalInsuranceNumberType,
  AddressLineType,
  ShortAddressLineType,
  PostCodeType,
  ISOCountryCodeType,
  AddressStructure,
  ContemporaryYearMonthType,
  CurrentAddressDateStructure,
  ResidentDateRangeStructure,
  PreviousAddressDateStructure,
  ContemporaryYearType,
  PhoneNumberType,
  YesNoType,
  LanguagePreferenceType,
  OtherNamesStructure,
  OtherForenames,
  OtherSurnames,
  ISOCountryDescType,
  CheckedByType,
  PassportNumberType,
  PassportStructure,
  DriverLicenceNumberType,
  DriverLicenceTypeType,
  DriverLicenceStructure,
  PartialEBulkApplicationData,
  EBulkApplicationDocument,
  EBulkApplicationDocuments,
  DBSTrackingStage,
  DBSTrackingEntry,
  DBSTracking,
  EBulkApplicationInternalNote,
  EBulkApplication,
  Sort_GetEBulkApplications,
  ColumnFilters_GetEBulkApplications,
  ReturnType_GetEBulkApplications,
  ReturnType_GetUninvoicedApplications,
  PostBody_MarkApplicationsAsInvoiced,
  x_dbs_api_clear_fields,
  AdditionalApplicantDetailsStructure,
  ApplicantIdentityDetailsStructure,
  ApplicantDetailsStructure,
  EBulkApplicationIssue,
  EBulkApplicationGateRefused,
  status,
  Price,
  ReturnType_GetEBulkApplicationPrice,
  StripeCheckout,
  StripeCheckoutsExisting,
  StripeStatus_NoChange,
  StripeStatus_Updated,
  StripeStatus,
  EBulkApplicationPayment,
  EBulkApplicationCorrection,
  EBulkApplicationCorrectionLog,
  PatchSet,
  EBulkApplicationCorrectionRefused,
  EBulkApplicationValidation,
  EBulkApplicationTransitionLog,
  SetISAReferenceEBulkApplication,
  SetInternalNoteEBulkApplication,
  RejectEBulkApplication,
  PostBody_EBulkApplicationMarkAsError,
  Pricing,
  PositionAppliedForType,
  OrganisationNameType,
  EmploymentDetailsStructure,
  RbNoType,
  CsNoType,
  DisclosureTypeType,
  RBApplicationReferenceType,
  RBdetailsStructure,
  eBulkApplicationStructure,
  columnFilters__6,
  EBulkApplicationsBatch,
  Sort_GetEBulkApplicationsBatches,
  ColumnFilters_GetEBulkApplicationsBatches,
  ReturnType_GetEBulkApplicationsBatches,
  EBulkBatchLeftOut,
  PostBody_EBulkApplicationsBatchSync,
  CRBResponse,
  ReturnType_EBulkApplicationsBatchSync,
  EBulkCertificateStatus,
  ReturnType_GetEBulkClientCertificate,
  PostBody_EBulkApplicationsBatchValidate,
  PostBody_StripeReconcile,
  PostBody_TestMoveEBulkApplication,
  PostBody_TestCreateEBulkApplicationsBatchCRB02,
  PostBody_TestCreateEBulkApplicationsBatchCRB03,
  PostBody_TestCreateEBulkApplicationsBatchCRB04,
  DBSTrackingSweepState,
  reconcileEBulkApplicationTracking_Body,
  AnalyticsBandCounts,
  ReturnType_GetAnalyticsStats,
  RecordEBulkApplicationConsents,
};

const endpoints = makeApi([
  {
    method: "get",
    path: "/customer",
    alias: "getCustomers",
    description: `Get all the customers the current user can see. Used for customer administration purposes. Staff see everyones. Customers see only theirs. Applicants don&#x27;t use this.`,
    requestFormat: "json",
    parameters: [
      {
        name: "page_index",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "page_size",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z
          .enum([
            "name asc",
            "name desc",
            "created_at asc",
            "created_at desc",
            "updated_at asc",
            "updated_at desc",
            "active asc",
            "active desc",
          ])
          .optional(),
      },
      {
        name: "globalFilter",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "columnFilters",
        type: "Query",
        schema: columnFilters__3,
      },
    ],
    response: ReturnType_GetCustomers,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/customer",
    alias: "createCustomer",
    description: `Create a new customer. Staff can customers. Customers don&#x27;t use this. Applicants don&#x27;t use this.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The customer to create`,
        type: "Body",
        schema: NewCustomer,
      },
    ],
    response: z.object({ inviteId: z.string().optional() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/customer/:id",
    alias: "getCustomerById",
    description: `Get the specified customer, showing details for table customer row expansion. Staff can see all customers. Customers can see themselves.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: Customer,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "patch",
    path: "/customer/:id",
    alias: "updateCustomer",
    description: `Update a customer, if it&#x27;s updateable. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Fields to update on the specified Customer`,
        type: "Body",
        schema: UpdateCustomer,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: Customer,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Customer not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "delete",
    path: "/customer/:id",
    alias: "archiveCustomer",
    description: `Archive a customer. Makes it inactive. Staff only`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ rescinded: z.boolean().optional() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/customer/:id/expand",
    alias: "getCustomerExpanded",
    description: `Get the specified customer, showing details for table customer row expansion. Staff can see all customers. Customers can see themselves.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ReturnType_GetCustomerExpanded,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/eBulkApplication",
    alias: "getEBulkApplications",
    description: `Get all the eBulkApplications the current user can see. Used for administration purposes. Staff see everyones. Customers see only theirs. Applicants don&#x27;t use this.`,
    requestFormat: "json",
    parameters: [
      {
        name: "page_index",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "page_size",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z
          .enum([
            "created_at asc",
            "created_at desc",
            "updated_at asc",
            "updated_at desc",
            "active asc",
            "active desc",
            "status asc",
            "status desc",
            "customer_name asc",
            "customer_name desc",
            "job_role asc",
            "job_role desc",
            "profile_given_name asc",
            "profile_given_name desc",
            "profile_family_name asc",
            "profile_family_name desc",
            "payment_at asc",
            "payment_at desc",
            "payment_method asc",
            "payment_method desc",
            "payment_reference asc",
            "payment_reference desc",
            "gdpr_consent asc",
            "gdpr_consent desc",
          ])
          .optional(),
      },
      {
        name: "globalFilter",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "columnFilters",
        type: "Query",
        schema: columnFilters__5,
      },
    ],
    response: ReturnType_GetEBulkApplications,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/eBulkApplication/invoiced",
    alias: "markApplicationsAsInvoiced",
    description: `Mark the specified applications as invoiced with a certain external_invoice_reference. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The applications to mark as invoiced`,
        type: "Body",
        schema: PostBody_MarkApplicationsAsInvoiced,
      },
    ],
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/eBulkApplication/uninvoiced",
    alias: "getUninvoicedApplications",
    description: `Get the applications which are uninvoiced and are ready to be batched. Staff only.`,
    requestFormat: "json",
    response: ReturnType_GetUninvoicedApplications,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/eBulkApplicationsBatch",
    alias: "getEBulkApplicationsBatches",
    description: `Get all the eBulkApplicationsBatches the current user can see. Used for administration purposes. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "page_index",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "page_size",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z
          .enum([
            "created_at asc",
            "created_at desc",
            "updated_at asc",
            "updated_at desc",
            "active asc",
            "active desc",
            "batch_number asc",
            "batch_number desc",
            "batch_message_type asc",
            "batch_message_type desc",
            "batch_filename asc",
            "batch_filename desc",
            "transferred_at asc",
            "transferred_at desc",
            "result_type asc",
            "result_type desc",
          ])
          .optional(),
      },
      {
        name: "globalFilter",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "columnFilters",
        type: "Query",
        schema: columnFilters__6,
      },
    ],
    response: ReturnType_GetEBulkApplicationsBatches,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/eBulkApplicationsBatch/clientCertificate",
    alias: "getEBulkClientCertificate",
    description: `The client certificate the api presents to the DBS FTPS service (EBULK_CLIENTCERT_CERTIFICATE_BASE64), with its expiry and a traffic-light status, plus the CA bundle used to verify the DBS server when one is configured. Staff only. Shown on the E-Bulk page so a certificate that is about to expire is seen every day (DBS-285).`,
    requestFormat: "json",
    response: ReturnType_GetEBulkClientCertificate,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/eBulkApplicationsBatch/dryrun",
    alias: "eBulkApplicationsBatchDryRun",
    description: `Create the EBulkApplicationsBatch, but don&#x27;t actually send it to the DBS, nor store it. Staff only.`,
    requestFormat: "json",
    response: z.object({
      xml: z.string(),
      leftOut: z.array(EBulkBatchLeftOut).optional(),
    }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/eBulkApplicationsBatch/process",
    alias: "processEBulkApplicationsBatches",
    description: `Process all the batches, so their applications are updated appropriately. All the results are stored within a journal.`,
    requestFormat: "json",
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/eBulkApplicationsBatch/sync",
    alias: "eBulkApplicationsBatchSync",
    description: `If CRB01 is enabled: Batch up all the eBulk applications in the &#x60;awaiting_submission_to_DBS&#x60; to the DBS. If CRB02/03/04 are enabled: Download those files from the DBS and update the applications. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `DBS Sync Configuration`,
        type: "Body",
        schema: PostBody_EBulkApplicationsBatchSync,
      },
    ],
    response: ReturnType_EBulkApplicationsBatchSync,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/eBulkApplicationsBatch/validate",
    alias: "validateEBulkApplicationsBatch",
    description: `Validate a specified batch. Recomputes the Message Check and validates it against the XML file.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `DBS Sync Configuration`,
        type: "Body",
        schema: z.object({ batch_id: z.string().uuid() }),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/eBulkApplicationTracking/reconcile",
    alias: "reconcileEBulkApplicationTracking",
    description: `Reconcile stored tracking state into application status changes. Every application with a proposed action lands in exactly one bucket: withdrawn (page explicitly says withdrawn -&gt; withdrawn status), or a certificate-dispatched age bucket (under 14 days / 14-28 days / over 28 days -&gt; completed status; a CRB04 usually follows within days, so young buckets are normally left to the batch flow). With confirm false (the default) this is a dry run returning stats and per-application rows. With confirm true, only the buckets listed in buckets are applied — each transition records its tracking evidence in the transition log and emails the customer&#x27;s notification profiles; rows in unselected buckets report outcome skipped. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: reconcileEBulkApplicationTracking_Body.optional(),
      },
    ],
    response: z.object({
      confirmed: z.boolean(),
      stats: z.object({
        applications: z.number().int(),
        withdrawn: z.number().int(),
        dispatched_under_14: z.number().int(),
        dispatched_14_to_28: z.number().int(),
        dispatched_over_28: z.number().int(),
        no_action: z.number().int(),
      }),
      rows: z.array(
        z.object({
          id: z.string().uuid(),
          dbs_enumber: z.string(),
          applicant_name: z.string().optional(),
          date_of_birth: z.string().optional(),
          checked_at: z.string(),
          bucket: z.enum([
            "withdrawn",
            "dispatched_under_14",
            "dispatched_14_to_28",
            "dispatched_over_28",
          ]),
          action: z.enum(["withdraw", "complete"]),
          event_date: z.string().optional(),
          outcome: z.enum(["applied", "failed", "skipped"]).optional(),
          error: z.string().optional(),
        })
      ),
    }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/eBulkApplicationTracking/report",
    alias: "getEBulkApplicationTrackingReport",
    description: `Validation report over every application the sweep tracks: the latest scraped tracking state per application with anything actionable flagged (withdrawal seen, certificate dispatched more than 14 days ago without a CRB04, unrecognised pages, applications never successfully checked). Staff only.`,
    requestFormat: "json",
    response: z.object({
      totals: z.object({
        applications: z.number().int(),
        tracked: z.number().int(),
        never_checked: z.number().int(),
        withdrawn_flagged: z.number().int(),
        dispatched_anomalies: z.number().int(),
        unrecognised: z.number().int(),
      }),
      rows: z.array(
        z.object({
          id: z.string().uuid(),
          ebulk_number: z.number().int().optional(),
          dbs_enumber: z.string(),
          latest: DBSTrackingEntry.optional(),
          history_count: z.number().int(),
          flag: z
            .enum([
              "withdrawn",
              "dispatched_anomaly",
              "unrecognised",
              "not_found",
              "never_checked",
            ])
            .optional(),
        })
      ),
    }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/eBulkApplicationTracking/sweep",
    alias: "sweepEBulkApplicationTracking",
    description: `Start a background sweep of the DBS tracking service covering every real in_progress application with an e-number that has not been successfully checked within the last hour (fresh ones are skipped and counted). Runs on this dyno at ~1 request per second (roughly 8 minutes for a full sweep), so the call returns immediately; poll the GET endpoint for progress, and abort via the abort endpoint. Only one sweep runs at a time — if one is already running, started is false. Sweeps only record tracking data; they never change application status (that is the reconcile endpoint, with staff confirmation). Staff only.`,
    requestFormat: "json",
    response: z.object({ started: z.boolean(), eligible: z.number().int() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/eBulkApplicationTracking/sweep",
    alias: "getEBulkApplicationTrackingSweep",
    description: `Progress of the currently-running tracking sweep (if any) and the outcome of the last completed one since this dyno started. Staff only.`,
    requestFormat: "json",
    response: z.object({
      current: DBSTrackingSweepState.nullable(),
      last_completed: DBSTrackingSweepState.nullable(),
    }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/eBulkApplicationTracking/sweep/abort",
    alias: "abortEBulkApplicationTrackingSweep",
    description: `Ask the running tracking sweep (if any) to stop after the in-flight application. aborting is false when no sweep is running. Staff only.`,
    requestFormat: "json",
    response: z.object({ aborting: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/invite",
    alias: "getInvites",
    description: `Get all the invites the current user can see. Used for invite administration purposes. Staff see everyones. Customers see only theirs. Applicants don&#x27;t use this.`,
    requestFormat: "json",
    parameters: [
      {
        name: "page_index",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "page_size",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z
          .enum([
            "type asc",
            "type desc",
            "active asc",
            "active desc",
            "accepted_at asc",
            "accepted_at desc",
            "email asc",
            "email desc",
            "customer_name asc",
            "customer_name desc",
            "job_role asc",
            "job_role desc",
            "invited_at asc",
            "invited_at desc",
            "invited_by_profile_name asc",
            "invited_by_profile_name desc",
          ])
          .optional(),
      },
      {
        name: "globalFilter",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "columnFilters",
        type: "Query",
        schema: columnFilters__2,
      },
    ],
    response: ReturnType_GetInvites,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/invite",
    alias: "createInvite",
    description: `Create a new invite. Staff can invite peers to be staff, customers, and applicants under a customer. Customers can invite their peers, and their applicants. Applicants don&#x27;t use this.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The invite to create`,
        type: "Body",
        schema: createInvite_Body,
      },
    ],
    response: z.object({ inviteId: z.string() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/invite/:id/accept",
    alias: "acceptInvite",
    description: `Accept a given invite for the current user. Uses the Auth0 ID token for the user&#x27;s email address. Matching is done by logged in email address, with invited effect attached to the current user&#x27;s profile. If the invite is for a staff member, the current user&#x27;s profile&#x27;s &#x60;is_staff&#x60; is set to true. If the invite is for a customer, the customer&#x27;s ID is added to the current user&#x27;s profile&#x27;s &#x60;admin_customer_ids&#x60;. If the invite is for an applicant, the customer&#x27;s ID is added to the current user&#x27;s profile&#x27;s &#x60;known_customer_ids&#x60;, and an application is added. The frontend should then get a new profile JWT token and refetch the applications to reflect these changes.`,
    requestFormat: "json",
    parameters: [
      {
        name: "x-auth0-id-token",
        type: "Header",
        schema: z.string(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ accepted: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/invite/:id/caseWorker",
    alias: "setCaseWorkerForInviteByID",
    description: `Assign, reassign or unassign the Case Worker on an open applicant invite. Staff or a manager of the invite&#x27;s customer only. The assignee must be an admin of the same customer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The case worker to assign, or null to unassign`,
        type: "Body",
        schema: z.object({
          case_worker_profile_id: z.string().uuid().nullable(),
        }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ updated: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Invite by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "delete",
    path: "/invite/:id/rescind",
    alias: "rescindInvite",
    description: `Rescind a given invite. Used for invite administration purposes. Staff can rescind any invite. Customers can rescind their invites (even if issued by staff) for both customers and applicants.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ rescinded: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/invite/my",
    alias: "getMyInvites",
    description: `Get the various invites for the current user to accept. Uses the Auth0 ID token for the user&#x27;s email address, as matching is done by logged in email address.`,
    requestFormat: "json",
    parameters: [
      {
        name: "x-auth0-id-token",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: z.array(
      z.union([MyInviteStaff, MyInviteCustomer, MyInviteApplicant])
    ),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/job",
    alias: "getJobs",
    description: `Get all the jobs the current user can see. Used for job administration purposes. Staff see everyones. Customers see only theirs. Applicants don&#x27;t use this.`,
    requestFormat: "json",
    parameters: [
      {
        name: "page_index",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "page_size",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z
          .enum([
            "role asc",
            "role desc",
            "application_workforce asc",
            "application_workforce desc",
            "application_type asc",
            "application_type desc",
            "working_with_adults asc",
            "working_with_adults desc",
            "working_with_children asc",
            "working_with_children desc",
            "working_at_home_address asc",
            "working_at_home_address desc",
            "volunteer asc",
            "volunteer desc",
            "customer_name asc",
            "customer_name desc",
            "created_at asc",
            "created_at desc",
            "updated_at asc",
            "updated_at desc",
            "active asc",
            "active desc",
          ])
          .optional(),
      },
      {
        name: "globalFilter",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "columnFilters",
        type: "Query",
        schema: columnFilters__4,
      },
    ],
    response: ReturnType_GetJobs,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/job",
    alias: "createJob",
    description: `Create a new job. They are only editable before the first submission. Staff can jobs. Customers can create their own. Applicants don&#x27;t use this.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The job to create`,
        type: "Body",
        schema: NewJob,
      },
    ],
    response: z.object({ jobId: z.string().uuid() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/job/:id",
    alias: "getJobById",
    description: `Get the specified job. Staff can see all jobs. Customers can see their own jobs.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: Job,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "patch",
    path: "/job/:id",
    alias: "updateJob",
    description: `Correct the role of an existing job. Staff only. Only the role can be changed, and only because a role that the DBS rejects cannot otherwise be fixed -- see DBS-237. Every change is recorded in the job&#x27;s change log.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ role: z.string().min(1).max(60) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: Job,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "delete",
    path: "/job/:id",
    alias: "archiveJob",
    description: `Archive a job. Makes it inactive. Staff can rescind any job. Customers can rescind their jobs.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ rescinded: z.boolean().optional() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/job/:id/applications/countersigned/count",
    alias: "getJobApplicationsCountersignedCount",
    description: `Get the number of countersigned applications for a job. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ countersignedApplicationsCount: z.number().int() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/job/:id/expand",
    alias: "getJobExpanded",
    description: `Get the specified job, showing details for table job row expansion. Staff can see all jobs. Customers can see their own jobs.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ReturnType_GetJobExpanded,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/profile",
    alias: "getProfiles",
    description: `Get all the profiles. Staff see everyones. Customers see only theirs. Applicants don&#x27;t use this.`,
    requestFormat: "json",
    parameters: [
      {
        name: "page_index",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "page_size",
        type: "Query",
        schema: z
          .string()
          .regex(/^[0-9]+$/)
          .optional(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z
          .enum([
            "created_at asc",
            "created_at desc",
            "updated_at asc",
            "updated_at desc",
            "active asc",
            "active desc",
          ])
          .optional(),
      },
      {
        name: "globalFilter",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "columnFilters",
        type: "Query",
        schema: columnFilters,
      },
    ],
    response: ReturnType_GetProfiles,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "patch",
    path: "/profile/:id/caseWorker",
    alias: "setCaseWorkerFlagForProfile",
    description: `Flag or un-flag a profile as a Case Worker for a customer. Staff or a manager (an admin of the customer who is not themselves a case worker for it) may do this, never on their own profile, and only when the customer has customer-managed case workers enabled. When un-flagging a profile that holds open invites or applications, those cases are transferred to transfer_to_profile_id (or unassigned when omitted) in the same transaction.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "customer_id",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "case_worker",
        type: "Query",
        schema: z.boolean(),
      },
      {
        name: "transfer_to_profile_id",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.object({
      transferred_invites: z.number().int(),
      transferred_applications: z.number().int(),
    }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "patch",
    path: "/profile/:id/countersignatory",
    alias: "addCountersignatoryNumberToProfile",
    description: `Staff can add a countersignatory number to a staff profile.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "countersignatory_number",
        type: "Query",
        schema: z.string().regex(/^[0-9]{11}$/),
      },
    ],
    response: z.object({ message: z.string().optional() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "delete",
    path: "/profile/:id/customerAdminAccess",
    alias: "removeCustomerAdminAccess",
    description: `Remove a profile from being an admin of a specified customer. Staff only`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "customer_id",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({ rescinded: z.boolean().optional() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/profile/my",
    alias: "getMyProfile",
    description: `Get profile information for the current user, optionally with a profile JWT token.`,
    requestFormat: "json",
    parameters: [
      {
        name: "jwt",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: ProfileResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Profile not found for this identity. User needs to create a profile first.`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/profile/my",
    alias: "createMyProfile",
    description: `Create a new profile for the current user.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The basics of new profile`,
        type: "Body",
        schema: createMyProfile_Body,
      },
      {
        name: "x-auth0-id-token",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string().optional() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Profile already exists for this identity.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "patch",
    path: "/profile/my",
    alias: "updateMyProfile",
    description: `Create a new profile for the current user.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Updateable profile aspects`,
        type: "Body",
        schema: createMyProfile_Body,
      },
    ],
    response: z.object({ message: z.string().optional() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/profile/my/emailPreferences",
    alias: "getMyProfileEmailPreferences",
    description: `Get the email preferences for the current user.`,
    requestFormat: "json",
    response: z.object({ application_tracking: z.boolean() }).partial(),
  },
  {
    method: "patch",
    path: "/profile/my/emailPreferences",
    alias: "updateMyProfileEmailPreferences",
    description: `Updates the email preferences for the current user.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Updateable profile aspects`,
        type: "Body",
        schema: z.object({ application_tracking: z.boolean() }).partial(),
      },
    ],
    response: z.object({ application_tracking: z.boolean() }).partial(),
  },
  {
    method: "get",
    path: "/public/eBulkApplication/:id",
    alias: "getEBulkApplicationByID",
    description: `Get a DBS eBulk application by ID (access checked against staff/customer/applicant).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "patch",
    path: "/public/eBulkApplication/:id",
    alias: "updateMyEBulkApplicationByID",
    description: `Update a DBS eBulk application for the current user.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Partial Applicant Details`,
        type: "Body",
        schema: PartialEBulkApplicationData,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "x-dbs-api-clear-fields",
        type: "Header",
        schema: x_dbs_api_clear_fields,
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id",
    alias: "completeMyEBulkApplicationByID",
    description: `Complete a DBS eBulk application for the current user.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Full Applicant Details`,
        type: "Body",
        schema: ApplicantDetailsStructure,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `DBS-305, gate 4 on the server: the stored application fails the DBS validation rules as a finished application, so it was not completed. The body lists the issues.`,
        schema: EBulkApplicationGateRefused,
      },
    ],
  },
  {
    method: "patch",
    path: "/public/eBulkApplication/:id/automatic-verification",
    alias: "manuallyTriggerAutomatedVerification",
    description: `Manually trigger automated verification for a customer&#x27;s applicant&#x27;s DBS eBulk application. Requires the customer to have automated verification enabled. Staff &amp; customer only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/changeJobRole",
    alias: "changeJobRoleForEBulkApplicationByID",
    description: `Change the job role for an eBulk application. Can be by customers while in or before the awaiting_payment state (for their applications). Can be by staff while in or before the awaiting_countersigning state. The job role must be in the same customer as the application.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "jobId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/completeChanges",
    alias: "completeChangesForEBulkApplicationByID",
    description: `Staff or the Customer can mark the changes as completed, sending it back to the awaiting_countersigning state.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/consents",
    alias: "recordMyEBulkApplicationConsents",
    description: `Record the consents and declarations the applicant has just given on one of the consent screens (DBS-266): which captures, on which screen, under which wording version (see api/src/api/services/consents/wordingVersions.ts and the TSS page &quot;Applicant consent screens: version map&quot;). Each capture is stamped server-side with the time and the applicant&#x27;s profile and appended to the application&#x27;s consent_log; a capture already recorded for the same screen and wording version is not duplicated. Owner only. Does not change gdpr_consent (see give-gdpr-consent).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The screen, its wording version and the captures ticked`,
        type: "Body",
        schema: RecordEBulkApplicationConsents,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 400,
        description: `Unknown wording version for that screen.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/public/eBulkApplication/:id/corrections",
    alias: "getCorrectionLogForEBulkApplicationByID",
    description: `Get all the corrections for a given eBulk application. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplicationCorrectionLog,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "patch",
    path: "/public/eBulkApplication/:id/corrections",
    alias: "addCorrectionsForEBulkApplicationByID",
    description: `Add corrections for a given eBulk application. Staff can correct any application. Customers can correct their own applications.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The corrections to add to the eBulk application. DBS-339: the merged application is checked against the DBS validation rules before it is saved; see PatchSet.acknowledge and the 422 response.`,
        type: "Body",
        schema: PatchSet,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `DBS-339: the correction would leave the application invalid under the DBS validation rules and &#x60;acknowledge&#x60; was not set, so nothing was saved. The body lists the issues.`,
        schema: EBulkApplicationCorrectionRefused,
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/countersign",
    alias: "countersignEBulkApplicationByID",
    description: `Countersign an eBulk application. Can be by staff while in the awaiting_countersigning state. Requires user to have a countersignatory_number on their profile.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `DBS-343, gate 2: the application fails the DBS validation rules (or the countersignatory or Registered Body number is not 11 digits, or the customer has not checked the current address), so it was not countersigned. The body lists the issues.`,
        schema: EBulkApplicationGateRefused,
      },
    ],
  },
  {
    method: "patch",
    path: "/public/eBulkApplication/:id/documents-applicant",
    alias: "updateMyEBulkApplicationDocumentsApplicantByID",
    description: `Update the documents an applicant intends to bring for a DBS eBulk application for the current user.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The documents that the applicant intends to bring`,
        type: "Body",
        schema: EBulkApplicationDocuments,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "patch",
    path: "/public/eBulkApplication/:id/documents-customer",
    alias: "confirmMyEBulkApplicationDocumentsCustomerByID",
    description: `Confirm the documents an customer has verified for their applicant&#x27;s DBS eBulk application. Moves to payment.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The documents that the applicant brought and were verified by the customer`,
        type: "Body",
        schema: EBulkApplicationDocuments,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/public/eBulkApplication/:id/eresult.pdf",
    alias: "getEBulkApplicationEResultPDF",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Application not completed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/error",
    alias: "errorMyEBulkApplicationByID",
    description: `If an eBulk application is rejected by the DBS, we need to mark it as &#x60;error_from_DBS&#x60;. Application must be in the &#x60;awaiting_response_from_DBS&#x60; status. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `DBS Sync Configuration`,
        type: "Body",
        schema: PostBody_EBulkApplicationMarkAsError,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/give-gdpr-consent",
    alias: "consentMyEBulkApplicationByID",
    description: `Mark an eBulk application as having GDPR consent given.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/payment",
    alias: "payForEBulkApplicationByID",
    description: `Record a payment against an application, while in &#x60;awaiting_payment&#x60; status. Staff payment is currently unsupported. Customers can pay for their applications, by any supported method, including invoice (if enabled for the customer). Applicants can pay for this own applications, but cannot choose invoice.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The documents that the applicant intends to bring`,
        type: "Body",
        schema: EBulkApplicationPayment,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/public/eBulkApplication/:id/price",
    alias: "priceForEBulkApplicationByID",
    description: `Get the price for a DBS eBulk application for the current user. Staff can see prices for any application. Customers can see prices for their applications. Applicants can see price for this own applications.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ReturnType_GetEBulkApplicationPrice,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/reject",
    alias: "rejectMyEBulkApplicationByID",
    description: `Reject an eBulk application. Can be done any time before payment by appliant / customer, or by staff at any time.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Rejection metadata`,
        type: "Body",
        schema: RejectEBulkApplication,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/requireChanges",
    alias: "requireChangesForEBulkApplicationByID",
    description: `During countersigning can request changes to be made to the applications. Can be done by staff while in the awaiting_countersigning state.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/resend",
    alias: "resendMyEBulkApplicationByID",
    description: `If an eBulk application is rejected by the DBS, once fixed we need to send it back so it goes off in another batch. Application must be in the &#x60;error_from_DBS&#x60; status. This is oly useful if the application is fixable (usually technical errors). Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `DBS-343, gate 2: the application still fails the DBS validation rules under its original countersignature, so it was not queued for sending again. The body lists the issues.`,
        schema: EBulkApplicationGateRefused,
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/restart",
    alias: "restartMyEBulkApplicationByID",
    description: `Restart an eBulk application. Application must be restartable, see the &#x60;/restartable&#x60; endpoint.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ invite_id: z.string() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/public/eBulkApplication/:id/restartable",
    alias: "restartableMyEBulkApplicationByID",
    description: `Is this eBulk application restartable. Application must be in the &#x60;rejected&#x60; state.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ isRestartable: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/returnToCountersigning",
    alias: "returnToCountersigningMyEBulkApplicationByID",
    description: `Return an eBulk application to the awaiting_countersigning state. Can be done by staff while in the awaiting_submission_to_DBS state. This typically happens when the application fails XSD validation late, and is indicative of an earlier validation bug.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/setCaseWorker",
    alias: "setCaseWorkerForEBulkApplicationByID",
    description: `Assign, reassign or unassign the Case Worker on an eBulk application. Staff or a manager of the application&#x27;s customer only. The assignee must be an admin of the same customer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The case worker to assign, or null to unassign`,
        type: "Body",
        schema: z.object({
          case_worker_profile_id: z.string().uuid().nullable(),
        }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/setInternalNote",
    alias: "setInternalNoteForEBulkApplicationByID",
    description: `Set the internal staff note for an eBulk application. Staff only. The note is stored on the application along with which profile saved it and when (stamped server-side), and returned in application responses, but must never be shown in customer or applicant UIs. Set an empty string to clear the note. No further change history is kept.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The internal note to store`,
        type: "Body",
        schema: z.object({ internal_note: z.string() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/public/eBulkApplication/:id/setISAReference",
    alias: "setISAReferenceForMyEBulkApplicationByID",
    description: `Set the ISA reference for an eBulk application. Can be done by staff once the application has been registered with the DBS. Must be in the &#x60;in_progress&#x60; state with a &#x60;dbs_enumber&#x60; and be ISA enabled in &#x60;documents_customer&#x60;.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Set ISA metadata`,
        type: "Body",
        schema: z.object({ isa_reference: z.string() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplication,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/public/eBulkApplication/:id/stripe/checkout",
    alias: "stripeCheckoutForEBulkApplicationByID",
    description: `Uses Stripe Checkout to creates a Session object for this DBS eBulk application for the current user. Stripe Checkout Sessions are reused for the same application within the expiry time (minus 1 hour). Payment by Stripe must be supported by the DBS eBulk application. Staff payment is currently unsupported. Customers can pay for their applications. Applicants can pay for this own applications.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "source_app",
        type: "Query",
        schema: z.enum(["applicant", "customer", "staff"]),
      },
      {
        name: "relative_return_path",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z
      .object({
        id: z.string(),
        payment_intent: z
          .union([
            z
              .object({ id: z.string(), object: z.literal("payment_intent") })
              .passthrough(),
            z.string(),
          ])
          .nullish(),
        payment_status: z
          .enum(["paid", "unpaid", "no_payment_required"])
          .nullish(),
        amount_total: z.number().nullish(),
        expires_at: z.number(),
        client_secret: z.string().nullish(),
      })
      .passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/public/eBulkApplication/:id/stripe/checkouts/existing",
    alias: "stripeCheckoutsExistingForEBulkApplicationByID",
    description: `Get the existing Stripe Checkout Session objects for this DBS eBulk application for the current user. This does NOT create nor update any Stripe Checkout Session. Validity of the sessions is not checked, and they may be expired. Staff payment is currently unsupported. Customers can pay for their applications. Applicants can pay for this own applications.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ checkouts: z.array(StripeCheckout) }).passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/public/eBulkApplication/:id/stripe/status",
    alias: "stripeStatusForEBulkApplicationByID",
    description: `Loads a Stripe Session object for this DBS eBulk application. Marks the application as paid if the payment was successful.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "session_id",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: StripeStatus,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/public/eBulkApplication/:id/transitions",
    alias: "getTransitionLogForEBulkApplicationByID",
    description: `Get all the transitions for a given eBulk application. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplicationTransitionLog,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/public/eBulkApplication/:id/validation",
    alias: "getValidationForEBulkApplicationByID",
    description: `DBS-343, gate 2: what the DBS validation rule set makes of the application as a finished application about to be countersigned by the caller: the applicant&#x27;s data, the identity check, the Registered Body and countersignatory numbers and the customer&#x27;s address check. Read-only; the countersign screen shows these reasons and the countersign and resend actions refuse while any remain. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EBulkApplicationValidation,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `DBS eBulk application by this ID not found for the current user.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/public/eBulkApplication/my",
    alias: "getMyEBulkApplications",
    description: `Get DBS eBulk applications for the current user.`,
    requestFormat: "json",
    response: z.array(EBulkApplication),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/public/pricing",
    alias: "getPricing",
    description: `Get the all the pricing.`,
    requestFormat: "json",
    response: Pricing,
  },
  {
    method: "get",
    path: "/stats/analytics",
    alias: "getAnalyticsStats",
    description: `Historical business analytics: monthly application / revenue / active-customer series, a daily series for rolling-average smoothing, and the predicted DBS bill with its line-item working. Only accessible by staff.`,
    requestFormat: "json",
    response: ReturnType_GetAnalyticsStats,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/stats/currentUsageStats",
    alias: "getCurrentUsageStats",
    description: `Get current usage stats. Only accessible by staff.`,
    requestFormat: "json",
    response: ReturnType_GetCurrentUsageStats,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/stats/global",
    alias: "getGlobalStats",
    description: `Get global stats. Only accessible by staff`,
    requestFormat: "json",
    parameters: [
      {
        name: "customer_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "assigned_to_me",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: ReturnType_GetGlobalStats,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/stripe/reconcile",
    alias: "reconcileStripeCheckouts",
    description: `Run a reconciliation process for the Stripe integration.
For all the Stripe checkout sessions associated with eBulk applications that are awaiting payment: * Optionally: update them from the Stripe API * Optionally: if the payment was successful, mark the application as paid.
If in dry-run mode, no changes are made.
Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PostBody_StripeReconcile,
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/system-info",
    alias: "getSystemInfo",
    description: `Get system information, used as a health check.`,
    requestFormat: "json",
    response: z.object({ alive: z.boolean() }),
  },
  {
    method: "get",
    path: "/test/connections/check",
    alias: "testConnectionsCheck",
    description: `Check the connections to the various services.`,
    requestFormat: "json",
    parameters: [
      {
        name: "httpDirect",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "httpProxy",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "ftpConnect",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "ftpList",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "ftpDownload",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "ftpDelete",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "ftpUpload",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.object({
      ok: z.boolean(),
      httpDirectResponse: z.union([z.boolean(), z.string()]).optional(),
      httpProxyResponse: z.union([z.boolean(), z.string()]).optional(),
      ftpConnectResponse: z.union([z.boolean(), z.string()]).optional(),
      ftpListResponse: z
        .union([
          z.boolean(),
          z.array(
            z
              .object({
                messageTypeKey: z.string(),
                response: z.object({}).partial().passthrough(),
                files: z.array(z.string()),
              })
              .partial()
              .passthrough()
          ),
        ])
        .optional(),
      ftpDownloadResponse: z
        .union([
          z.boolean(),
          z.array(
            z
              .object({
                messageTypeKey: z.string(),
                filename: z.string(),
                response: z.object({}).partial().passthrough(),
              })
              .partial()
              .passthrough()
          ),
        ])
        .optional(),
      ftpDeleteResponse: z
        .union([
          z.boolean(),
          z.array(
            z
              .object({
                messageTypeKey: z.string(),
                filename: z.string(),
                response: z.object({}).partial().passthrough(),
              })
              .partial()
              .passthrough()
          ),
        ])
        .optional(),
      ftpUploadResponse: z
        .union([z.boolean(), z.object({}).partial().passthrough()])
        .optional(),
    }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/test/data/snapshot",
    alias: "getTestDataSnapshots",
    description: `Get a list of the DB snapshots we have available for testing.`,
    requestFormat: "json",
    response: z.array(z.object({ name: z.string() })),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/test/data/snapshot",
    alias: "restoreTestDataSnapshot",
    description: `Restore a DB snapshots from those have available for testing.`,
    requestFormat: "json",
    parameters: [
      {
        name: "name",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({ restored: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/test/eBulkApplications/:id/move",
    alias: "testMoveEBulkApplication",
    description: `Move an application to a different profile. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `The profile to move the application to`,
        type: "Body",
        schema: PostBody_TestMoveEBulkApplication,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/test/eBulkApplicationsBatch/CRB01",
    alias: "testCreateEBulkApplicationsBatchCRB01",
    description: `Create a DBS eBulk application batch for testing a CRB01 offline. This is the same as sending a CRB01 file to the DBS, but without actually sending it to the FTPS server and faking the result. This means it will generate the CRB01 as normal and do everything except for upload the file. Staff only.`,
    requestFormat: "json",
    response: z.object({
      batchIds: z.array(z.string()),
      leftOut: z.array(EBulkBatchLeftOut).optional(),
    }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/test/eBulkApplicationsBatch/CRB02",
    alias: "testCreateEBulkApplicationsBatchCRB02",
    description: `Create a fake CRB02 response for a given CRB01 batch. This mocks the list and download of the CRB02 file from the DBS, then responds accordingly. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PostBody_TestCreateEBulkApplicationsBatchCRB02,
      },
    ],
    response: z.object({ batchIds: z.array(z.string()) }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/test/eBulkApplicationsBatch/CRB03",
    alias: "testCreateEBulkApplicationsBatchCRB03",
    description: `Create a fake CRB03 response for a set of applications, with given results (OK or ERROR codes). This mocks the list and download of the CRB03 file from the DBS, then responds accordingly. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PostBody_TestCreateEBulkApplicationsBatchCRB03,
      },
    ],
    response: z.object({ batchIds: z.array(z.string()) }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "post",
    path: "/test/eBulkApplicationsBatch/CRB04",
    alias: "testCreateEBulkApplicationsBatchCRB04",
    description: `Create a fake CRB04 response for a set of applications, with given results. This mocks the list and download of the CRB02 file from the DBS, then responds accordingly. Staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PostBody_TestCreateEBulkApplicationsBatchCRB04,
      },
    ],
    response: z.object({ batchIds: z.array(z.string()) }),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: "get",
    path: "/unused",
    alias: "unused",
    description: `NOTE: This endpoint is unused. It&#x27;s here to get &quot;#/components/schemas/CompleteEBulkApplication&quot; into the generated zod client.`,
    requestFormat: "json",
    response: z.array(eBulkApplicationStructure),
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
